/**
 * useDeviceOcr — Device-native OCR text extraction (DRR-159)
 *
 * Extracts text from captured images using device-native APIs:
 * - Chrome/Android: Shape Detection API (BarcodeDetector, TextDetector)
 * - iOS Safari: Not yet supported — falls back to canvas + manual
 *
 * For production use, the primary flow is:
 * 1. Phone captures image
 * 2. Device OCR extracts text on-device (T4 stays local)
 * 3. Only extracted text sent to FERP (no image upload for OCR)
 * 4. Image upload only if document needs storing (e.g., attach to FHIR)
 *
 * When device OCR is unavailable, the image is uploaded and
 * server-side processing handles extraction.
 *
 * FERP Principles:
 * - DRR-114: T4 raw media processed on-device, never uploaded for OCR
 * - DRR-128: Write-time minimization — only structured text leaves device
 * - No frontend ML library imports (DRR-159: device-native APIs only)
 */

import { useCallback, useState } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

export interface DeviceOcrResult {
  /** Extracted text from the image */
  text: string;
  /** Whether device-native OCR was used (vs fallback) */
  deviceNative: boolean;
  /** Number of text blocks detected */
  blockCount: number;
}

export interface UseDeviceOcrResult {
  /** Extract text from a Blob (image) using device-native OCR */
  extractText: (imageBlob: Blob) => Promise<DeviceOcrResult | null>;
  /** Whether OCR is in progress */
  processing: boolean;
  /** Whether device-native OCR is available */
  isAvailable: boolean;
  /** Last extraction result */
  result: DeviceOcrResult | null;
  /** Error from last extraction */
  error: Error | null;
}

// ── Availability Detection ───────────────────────────────────────────────────

/** Check if the experimental TextDetector API is available (Chrome 70+, Android) */
function isTextDetectorAvailable(): boolean {
  return typeof window !== 'undefined' && 'TextDetector' in window;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useDeviceOcr(): UseDeviceOcrResult {
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<DeviceOcrResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const isAvailable = isTextDetectorAvailable();

  const extractText = useCallback(
    async (imageBlob: Blob): Promise<DeviceOcrResult | null> => {
      setProcessing(true);
      setError(null);

      try {
        if (!isTextDetectorAvailable()) {
          // Device OCR not available — return null so caller can fall back
          // to server-side processing or manual entry
          setProcessing(false);
          return null;
        }

        // Create ImageBitmap from blob for TextDetector
        const bitmap = await createImageBitmap(imageBlob);

        // Use the experimental TextDetector API
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const detector = new (window as any).TextDetector();
        const detectedTexts = await detector.detect(bitmap);
        bitmap.close();

        // Concatenate all detected text blocks
        const textBlocks: string[] = detectedTexts.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (block: any) => block.rawValue || '',
        );
        const fullText = textBlocks.filter(Boolean).join('\n');

        const ocrResult: DeviceOcrResult = {
          text: fullText,
          deviceNative: true,
          blockCount: textBlocks.length,
        };

        setResult(ocrResult);
        setProcessing(false);
        return ocrResult;
      } catch (err) {
        const ocrError =
          err instanceof Error ? err : new Error('Device OCR failed');
        setError(ocrError);
        setResult(null);
        setProcessing(false);
        return null;
      }
    },
    [],
  );

  return { extractText, processing, isAvailable, result, error };
}
